import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { usePerformanceYear } from "@/contexts/PerformanceYearContext";

interface BandPercentages {
  A: number;
  B: number;
  C: number;
  D: number;
}

export const useBandingSimulation = (currentUser: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const { activeYear } = usePerformanceYear();

  // Function to convert percentages to boundaries based on actual data range
  const convertPercentagesToBoundaries = (percentages: BandPercentages, dataRange?: { min: number; max: number }) => {
    // If no data range provided, use default 0-5 scale
    if (!dataRange) {
      const cumulativeD = percentages.D;
      const cumulativeC = cumulativeD + percentages.C;
      const cumulativeB = cumulativeC + percentages.B;
      
      const line1 = (cumulativeD / 100) * 5;
      const line2 = (cumulativeC / 100) * 5;
      const line3 = (cumulativeB / 100) * 5;
      const line4 = 5.0;
      
      return {
        line1: Math.round(line1 * 100) / 100,
        line2: Math.round(line2 * 100) / 100,
        line3: Math.round(line3 * 100) / 100,
        line4: line4
      };
    }

    // Use actual data range
    const range = dataRange.max - dataRange.min;
    
    // If all ratings are the same, use small offsets
    if (range === 0) {
      return {
        line1: dataRange.min + 0.1,
        line2: dataRange.min + 0.2,
        line3: dataRange.min + 0.3,
        line4: dataRange.max
      };
    }

    const cumulativeD = percentages.D;
    const cumulativeC = cumulativeD + percentages.C;
    const cumulativeB = cumulativeC + percentages.B;
    
    const line1 = dataRange.min + (cumulativeD / 100) * range;
    const line2 = dataRange.min + (cumulativeC / 100) * range;
    const line3 = dataRange.min + (cumulativeB / 100) * range;
    const line4 = dataRange.max;
    
    return {
      line1: Math.round(line1 * 100) / 100,
      line2: Math.round(line2 * 100) / 100,
      line3: Math.round(line3 * 100) / 100,
      line4: Math.round(line4 * 100) / 100
    };
  };

  const loadBandingSimulation = async (): Promise<BandPercentages | null> => {
    if (!currentUser) return null;
    
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('banding_simulation')
        .select('band_a, band_b, band_c, band_d')
        .eq('user_id', currentUser.id)
        .eq('year', activeYear)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error("Error loading banding simulation:", error);
        return null;
      }

      if (data) {
        return {
          A: data.band_a,
          B: data.band_b,
          C: data.band_c,
          D: data.band_d
        };
      }

      return null;
    } catch (error) {
      console.error("Error loading banding simulation:", error);
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  const saveBandingSimulation = async (bandPercentages: BandPercentages) => {
    if (!currentUser) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('banding_simulation')
        .upsert({
          user_id: currentUser.id,
          year: activeYear,
          band_a: bandPercentages.A,
          band_b: bandPercentages.B,
          band_c: bandPercentages.C,
          band_d: bandPercentages.D
        }, {
          onConflict: 'user_id,year'
        });

      if (error) {
        console.error("Error saving banding simulation:", error);
        toast.error("Error saving banding simulation");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error saving banding simulation:", error);
      toast.error("Error saving banding simulation");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const resetBandingSimulation = async () => {
    if (!currentUser) return false;

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('banding_simulation')
        .upsert({
          user_id: currentUser.id,
          year: activeYear,
          band_a: 10,
          band_b: 25,
          band_c: 55,
          band_d: 10
        }, {
          onConflict: 'user_id,year'
        });

      if (error) {
        console.error("Error resetting banding simulation:", error);
        toast.error("Error resetting banding simulation");
        return false;
      }

      return true;
    } catch (error) {
      console.error("Error resetting banding simulation:", error);
      toast.error("Error resetting banding simulation");
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    loadBandingSimulation,
    saveBandingSimulation,
    resetBandingSimulation,
    convertPercentagesToBoundaries
  };
};
